import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "./api";
import useIdleLogout from "./useIdleLogout";
import {
  handleDownloadPrefilledPDF,
  handleDownloadConsentPDF,
  handleDownloadBillingPDF,
  handleDownloadCombinedPDF,
  handleViewCombinedPDF,
  handleDownloadAneasthetistPDF,
} from "./pdfGenerators";



const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {});
};

const downloadAndSave = async (label, patient) => {
  if (!patient) return;

  let pdfBlob = null;
  let field = "";

  if (label === "Detail_Form") {
    pdfBlob = await handleDownloadPrefilledPDF(patient, true);
    field = "attachment_download_01";
  }
  if (label === "Consent_Form") {
    pdfBlob = await handleDownloadConsentPDF(patient, true);
    field = "attachment_download_02";
  }
  if (label === "Billing_Form") {
    pdfBlob = await handleDownloadBillingPDF(patient, true);
    field = "attachment_download_03";
  }
  if (label === "All_Forms") {
    pdfBlob = await handleDownloadCombinedPDF(patient, true);
    field = "attachment_download_all";
  }

  if (!pdfBlob) return;

  const date = new Date();
  const timestamp = `${date.getFullYear()}-${String(
    date.getMonth() + 1
  ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}-${String(
    date.getHours()
  ).padStart(2, "0")}${String(date.getMinutes()).padStart(2, "0")}`;
  const filename = `${patient.request_number}_${label}_${patient.patient_surname}_${timestamp}.pdf`;

  const blob = new Blob([pdfBlob], { type: "application/pdf" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);

  // Track the download
  try {
    const token = sessionStorage.getItem("token");
    await api.post(
      `/patients/${patient.request_number}/track-download`,
      { field },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (err) {
    console.error("Failed to track download", err);
  }
};

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const { showWarning, stayLoggedIn } = useIdleLogout();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}${String(date.getDate()).padStart(2, "0")}${String(
      date.getHours()
    ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    const yyyy = date.getFullYear();
    const mm = (date.getMonth() + 1).toString().padStart(2, "0");
    const dd = date.getDate().toString().padStart(2, "0");
    return `${yyyy}${mm}${dd}`;
  };

  const displayValue = (key, value) => {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (key === "rec_cre_dte") return formatDateTime(value);
    if (key.includes("date_of_birth")) return formatDateOnly(value);
    return value || "";
  };

  const handleVerify = async () => {
    try {
      const token = sessionStorage.getItem("token");

      await api.post(
        "/verify",
        {
          requestNumber: patient.request_number,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccessMessage("Patient sent to verification screen.");

      setTimeout(() => {
          setSuccessMessage("");
      }, 3000);
    } catch (err) {
      setSuccessMessage(
        err.response?.data?.message || "Unable to start verification."
    );
    
    setTimeout(() => {
        setSuccessMessage("");
    }, 4000);
    }
  };

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    api
      .get(`patients/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setPatient(res.data);
      })
      .catch((err) => {
        console.error(err);
        navigate("/patients");
      });
  }, [id, navigate]);

  if (!patient)
    return <div className="text-center mt-20 text-gray-600">Loading...</div>;

  const sectionFields = {
    "Main Member Information": [
      "main_identification_number",
      "main_surname",
      "main_full_names",
      "main_initials",
      "main_title",
      "main_male",
      "main_female",
      "main_date_of_birth",
      "main_employer",
      "main_cell_number",
      "main_home_number",
      "main_work_number",
      "main_fax_number",
      "main_email_address",
      "main_email_statements",
      "main_postal_address_1",
      "main_postal_address_2",
      "main_postal_address_3",
      "main_postal_address_code",
      "main_physical_address_1",
      "main_physical_address_2",
      "main_physical_address_3",
      "main_physical_address_code",
    ],
    "Medical Aid Information": [
      "main_medical_scheme",
      "main_medical_scheme_plan",
      "main_medical_member_number",
      "main_medical_member_main_dep_code",
      "main_medical_gap_cover",
    ],
    "Patient Information": [
      "patient_identification_number",
      "patient_surname",
      "patient_full_names",
      "patient_nick_names",
      "patient_initials",
      "patient_title",
      "patient_male",
      "patient_female",
      "patient_date_of_birth",
      "patient_cell_number",
      "patient_use_appoint_test_results",
      "patient_home_number",
      "patient_work_number",
      "patient_email_address",
      "patient_occupation",
      "patient_marital_status",
      "patient_relationship_main",
      "patient_dep_code",
      "patient_age",
      "patient_height",
      "patient_weight",
      "patient_referring_doctor",
      "patient_referring_doctor_nuiber",
      "patient_gp",
      "patient_gp_number",
    ],
    "Next of Kin Information": [
      "next_kin_initials",
      "next_kin_title",
      "next_kin_surname",
      "next_kin_full_names",
      "next_kin_cell_number",
      "next_kin_relationship",
    ],
    Signature: ["signature_image"],
  };

  const formatLabel = (key) => {
    return key
      .replace(/^(main_|patient_|next_kin_)/, "") // Remove leading context
      .replace(/_/g, " ") // Convert underscores to spaces
      .replace(/\b\w/g, (l) => l.toUpperCase()); // Capitalize each word
  };

  return (
    <div
      className="min-h-screen w-full bg-cover bg-center text-gray-800"
      style={{
        backgroundImage: `radial-gradient(circle at top left, #A8A8A8, #8C8C8C, #85D3DE, #8DDFEB)`,
      }}
    >
      <div className="flex justify-center pt-6">
        <img
          src="/InfoMedi_logo.png"
          alt="InfoMedi Logo"
          className="h-20 drop-shadow"
        />
      </div>

      <div className="bg-white/30 backdrop-blur-md shadow-xl rounded-xl px-6 py-8 mt-8 max-w-7xl mx-auto border border-white/40">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="flex flex-col gap-2">
            <button
              onClick={() => handleDownloadPrefilledPDF(patient)}
              className="bg-gradient-to-r from-gray-400 to-blue-800 hover:from-blue-600 hover:to-gray-700 text-white py-2 rounded-lg shadow-md transition-all"
            >
              VIEW DETAIL FORM
            </button>
            <button
              onClick={() => handleDownloadConsentPDF(patient)}
              className="bg-gradient-to-r from-gray-400 to-blue-800 hover:from-blue-600 hover:to-gray-700 text-white py-2 rounded-lg shadow-md transition-all"
            >
              VIEW CONSENT FORM
            </button>
            <button
              onClick={() => handleDownloadBillingPDF(patient)}
              className="bg-gradient-to-r from-gray-400 to-blue-800 hover:from-blue-600 hover:to-gray-700 text-white py-2 rounded-lg shadow-md transition-all"
            >
              VIEW BILLING FORM
            </button>
            <button
              onClick={() => handleViewCombinedPDF(patient)}
              className="bg-gradient-to-r from-gray-400 to-blue-800 hover:from-blue-600 hover:to-gray-700 text-white py-2 rounded-lg shadow-md transition-all"
            >
              VIEW ALL FORMS
            </button>
            <button
              onClick={() => handleDownloadAneasthetistPDF(patient)}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white py-2 rounded-lg shadow-md transition-all"
            >
              VIEW ANEASTHETIST FORM
            </button>
          </div>

          <div className="flex flex-col gap-2">
            <button
              onClick={() => downloadAndSave("Detail_Form", patient)}
              className="bg-gradient-to-r from-gray-400 to-blue-800 hover:from-blue-600 hover:to-gray-700 text-white py-2 rounded-lg shadow-md transition-all"
            >
              DOWNLOAD DETAIL FORM
            </button>
            <button
              onClick={() => downloadAndSave("Consent_Form", patient)}
              className="bg-gradient-to-r from-gray-400 to-blue-800 hover:from-blue-600 hover:to-gray-700 text-white py-2 rounded-lg shadow-md transition-all"
            >
              DOWNLOAD CONSENT FORM
            </button>
            <button
              onClick={() => downloadAndSave("Billing_Form", patient)}
              className="bg-gradient-to-r from-gray-400 to-blue-800 hover:from-blue-600 hover:to-gray-700 text-white py-2 rounded-lg shadow-md transition-all"
            >
              DOWNLOAD BILLING FORM
            </button>
            <button
              onClick={() => downloadAndSave("All_Forms", patient)}
              className="bg-gradient-to-r from-gray-400 to-blue-800 hover:from-blue-600 hover:to-gray-700 text-white py-2 rounded-lg shadow-md transition-all"
            >
              DOWNLOAD ALL FORMS
            </button>
          </div>

          <div className="flex flex-col gap-2">
            <button
              onClick={handleVerify}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white py-2 rounded-lg shadow-md transition-all"
            >
              VERIFY
            </button>
            <button
              onClick={() => navigate(`/patients/${id}/edit`)}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white py-2 rounded-lg shadow-md transition-all"
            >
              EDIT
            </button>
            <button
              onClick={async () => {
                setDeletePassword("");
                setDeleteError("");
                setDeleteSuccess("");

                try {
                  const token = sessionStorage.getItem("token");
                  const res = await api.get(`patients/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  setPatient(res.data); // update state with fresh data
                } catch (err) {
                  console.error("Failed to refresh patient:", err);
                }

                setShowDeleteModal(true);
              }}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white py-2 rounded-lg shadow-md transition-all"
            >
              DELETE
            </button>
            <button
              onClick={() => navigate("/patients")}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white py-2 rounded-lg shadow-md transition-all"
            >
              BACK TO LIST
            </button>
            <button
              onClick={() => {
                sessionStorage.removeItem("token");
                navigate("/");
              }}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white py-2 rounded-lg shadow-md transition-all"
            >
              LOGOUT
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="grid sm:grid-cols-2 gap-x-12 text-sm text-gray-800">
            <div>
              <strong className="text-blue-700">Request Number:</strong>{" "}
              {patient.request_number}
              <span className="ml-6">
                <strong className="text-blue-700">Follow-up?</strong>{" "}
                {patient.follow_up ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </div>

        {Object.entries(sectionFields).map(([sectionTitle, keys]) => {
          const half = Math.ceil(keys.length / 2);
          const col1 = keys.slice(0, half);
          const col2 = keys.slice(half);
          return (
            <div key={sectionTitle} className="mb-6">
              <h2 className="text-xl font-bold text-blue-700 border-b border-blue-200 mb-4 pb-1">
                {sectionTitle}
              </h2>
              <div className="grid sm:grid-cols-2 gap-x-12 text-sm text-gray-800">
                <div className="flex flex-col gap-2">
                  {col1.map((key) =>
                    key === "signature_image" && patient[key] ? (
                      <div key={key}>
                        <strong className="text-blue-700">
                          {formatLabel(key)}:
                        </strong>
                        <br />
                        <img
                          src={`data:image/png;base64,${patient[key]}`}
                          alt="Signature"
                          className="border rounded mt-2 max-w-full"
                        />
                      </div>
                    ) : (
                      <div key={key}>
                        <strong className="text-blue-700">
                          {formatLabel(key)}:
                        </strong>{" "}
                        {displayValue(key, patient[key])}
                        {key !== "request_number" &&
                          key !== "signature_image" && (
                            <button
                              onClick={() =>
                                copyToClipboard(displayValue(key, patient[key]))
                              }
                              className="ml-2 text-xs text-black-500 border border-blue-300 px-1 py-0.5 rounded hover:bg-blue-100"
                              title="Copy to clipboard"
                            >
                              Copy
                            </button>
                          )}
                      </div>
                    )
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {col2.map((key) =>
                    key === "signature_image" && patient[key] ? (
                      <div key={key}>
                        <strong className="text-blue-700">
                          {formatLabel(key)}:
                        </strong>
                        <br />
                        <img
                          src={`data:image/png;base64,${patient[key]}`}
                          alt="Signature"
                          className="border rounded mt-2 max-w-full"
                        />
                      </div>
                    ) : (
                      <div key={key}>
                        <strong className="text-blue-700">
                          {formatLabel(key)}:
                        </strong>{" "}
                        {displayValue(key, patient[key])}
                        {key !== "request_number" && (
                          <button
                            onClick={() =>
                              copyToClipboard(displayValue(key, patient[key]))
                            }
                            className="ml-2 text-xs text-black-500 border border-blue-300 px-1 py-0.5 rounded hover:bg-blue-100"
                            title="Copy to clipboard"
                          >
                            Copy
                          </button>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Confirm Deletion
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Please enter your password to confirm deletion of this patient.
            </p>
            <input
              type="password"
              placeholder="Enter your password"
              className="w-full p-2 border border-gray-300 rounded mb-3"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
            />
            {deleteError && (
              <p className="text-red-600 text-sm mb-2">{deleteError}</p>
            )}
            {deleteSuccess && (
              <p className="text-green-700 text-sm mb-2">{deleteSuccess}</p>
            )}
            <div className="flex justify-between">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeleteError("");

                  const storedPassword = sessionStorage.getItem("password");
                  if (deletePassword !== storedPassword) {
                    setDeleteError("Incorrect password.");
                    return;
                  }

                  const hasAllThree =
                    patient.attachment_download_01 &&
                    patient.attachment_download_02 &&
                    patient.attachment_download_03;
                  const hasCombined = patient.attachment_download_all;

                  if (!(hasAllThree || hasCombined)) {
                    setDeleteError(
                      "Cannot delete, please ensure either all individual forms or the combined form is downloaded!"
                    );
                    return;
                  }

                  try {
                    const token = sessionStorage.getItem("token");
                    await api.post(
                      `/patients/${id}/delete`,
                      {
                        username: sessionStorage.getItem("username"),
                        password: deletePassword,
                      },
                      {
                        headers: { Authorization: `Bearer ${token}` },
                      }
                    );

                    setDeleteSuccess("Patient successfully deleted.");
                    setTimeout(() => navigate("/patients"), 1500);
                  } catch (err) {
                    setDeleteError("Deletion failed. Try again.");
                  }
                }}
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showWarning && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 text-yellow-900 border border-yellow-400 px-4 py-2 rounded shadow-lg z-50">
          <p>You will be logged out due to inactivity in 30 seconds.</p>
          <button
            onClick={stayLoggedIn}
            className="mt-2 px-3 py-1 bg-blue-600 text-white rounded"
          >
            Stay Logged In
          </button>
        </div>
      )}
      {successMessage && (
    <div className="fixed top-6 right-6 z-50 animate-fade-in">
        <div className="bg-green-600 text-white px-6 py-4 rounded-xl shadow-2xl">
            ✓ {successMessage}
        </div>
    </div>
)}
    </div>
    
  );
}
